import { CalendarDays, ChevronRight, Heart, MessageCircle } from "lucide-react";
import { useState } from "react";
import { AspirasiItem } from "../../Pages/HeroAspirasi/AspirasiList";
import { toggleLike } from "../../../utils/aspirasiApi";

interface Props {
  data: AspirasiItem;
  deviceId: string;
  onClick: () => void;
  onPatch: (patch: Partial<AspirasiItem>) => void;
}

const formatTanggal = (date: string) => {
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date(date));
};

const getInitial = (nama: string) => {
  return nama
    .split(" ")
    .map((item) => item[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

const AspirasiCard = ({ data, deviceId, onClick, onPatch }: Props) => {
  const [liking, setLiking] = useState(false);

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation(); // biar gak ikut buka modal detail

    if (liking) return;
    setLiking(true);

    // Optimistic update dulu, biar kerasa instant
    const prevLiked = data.liked_by_me;
    const prevTotal = data.total_like;

    onPatch({
      liked_by_me: !prevLiked,
      total_like: prevLiked ? prevTotal - 1 : prevTotal + 1,
    });

    try {
      const res = await toggleLike(data.id, deviceId);

      if (res?.success) {
        onPatch({ liked_by_me: res.liked, total_like: res.total_like });
      } else {
        // gagal, balikin ke kondisi semula
        onPatch({ liked_by_me: prevLiked, total_like: prevTotal });
      }
    } catch {
      onPatch({ liked_by_me: prevLiked, total_like: prevTotal });
    } finally {
      setLiking(false);
    }
  };

  const jumlahKomentar = data.komentar?.length ?? 0;

  return (
    <div
      onClick={onClick}
      className="
      group
      relative
      cursor-pointer
      overflow-hidden
      rounded-3xl
      border
      border-lime-100
      bg-white
      p-6
      
      shadow-[0_20px_60px_rgba(132,204,22,0.22)]
      
      transition-all
      duration-500
      
      hover:-translate-y-2
      hover:border-lime-300
      hover:shadow-[0_8px_30px_rgba(132,204,22,0.10)]
      ">
      {/* Header */}

      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div
            className="
              h-14
              w-14
              rounded-2xl
              bg-gradient-to-br
              from-lime-500
              to-green-600
              flex
              items-center
              justify-center
              text-lg
              font-bold
              text-white
              shadow-lg
            ">
            {getInitial(data.nama)}
          </div>

          <div>
            <h3 className="font-bold text-lg text-slate-800">{data.nama}</h3>

            <p className="text-sm text-gray-500">{data.no_hp}</p>
          </div>
        </div>

        <span
          className="
            rounded-full
            bg-lime-100
            px-3
            py-1
            text-xs
            font-semibold
            text-lime-700
          ">
          {data.status}
        </span>
      </div>

      {/* Alamat */}

      {/* <div className="mt-5 flex items-center gap-2 text-gray-500">
        <MapPin size={17} />

        <span className="line-clamp-1 text-sm">{data.alamat}</span>
      </div> */}

      {/* Aspirasi */}

      <div className="mt-5">
        <p className="line-clamp-3 leading-7 text-gray-700">{data.aspirasi}</p>
      </div>

      {/* Like & Komentar */}

      <div className="mt-5 flex items-center gap-4 border-t pt-4">
        <button
          type="button"
          onClick={handleLike}
          disabled={liking}
          className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${data.liked_by_me ? "bg-lime-100 text-lime-700" : "text-gray-500 hover:bg-gray-100"}`}>
          <Heart size={17} className={data.liked_by_me ? "fill-lime-600 text-lime-600" : ""} />
          {data.total_like}
        </button>

        <button type="button" onClick={onClick} className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold text-gray-500 hover:bg-gray-100">
          <MessageCircle size={17} />
          {jumlahKomentar}
        </button>
      </div>

      {/* Footer */}

      <div className="mt-4 flex items-center justify-between border-t pt-5">
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <CalendarDays size={16} />

          <span>{formatTanggal(data.tanggal)}</span>
        </div>

        <button
          className="
            flex
            items-center
            gap-1
            font-semibold
            text-lime-600
            transition-all
            group-hover:translate-x-1
          ">
          Detail
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default AspirasiCard;
